import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Search, Users, Phone, GraduationCap, Bell, Zap, Settings as SettingsIcon,
  X, MessageCircle, Mail, CreditCard, UserPlus, Receipt, Calendar,
  TrendingUp, Activity, DollarSign, AlertTriangle, Sparkles, Command,
  Wallet, ShieldAlert, FileText, ClipboardList, Star, Clock, BookOpen, PieChart, ShieldCheck, Trophy,
} from 'lucide-react';
import { useAdminShellActive } from '../components/admin-mobile/useAdminNavPrefs';
import api from '../lib/api';
import './DnaCommandCenter.css';

// ─────────────────────  LocalStorage helpers (V3.2)  ─────────────────────
const LS_RECENT = 'dna_v3_recent_students';
const LS_FAVS = 'dna_v3_favorites';
const LS_RECENT_SEARCH = 'dna_v3_recent_search';
const LS_PREFS_CACHE = 'dna_v3_prefs_cache';
const LS_PREFS_MIGRATED = 'dna_v3_prefs_migrated_v1';

const readLS = (k, fallback) => {
  try { return JSON.parse(localStorage.getItem(k) || '') || fallback; } catch { return fallback; }
};
const writeLS = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* noop */ } };

// ─────────────────────  PREFS STORE V3.x (backend-backed, single source of truth)  ─────────────────────
// Substitui o uso direto de localStorage para histórico de buscas + recentes + favoritos.
// Mantém cache local (LS_PREFS_CACHE) apenas para resiliência offline.
const _prefsState = { recent_searches: [], recent_students: [], favorites: [], _loaded: false, _loading: null };
const _prefsListeners = new Set();
const _notifyPrefs = () => _prefsListeners.forEach((fn) => { try { fn(); } catch { /* noop */ } });
const _persistCache = () => writeLS(LS_PREFS_CACHE, {
  recent_searches: _prefsState.recent_searches,
  recent_students: _prefsState.recent_students,
  favorites: _prefsState.favorites,
});

async function loadPrefs() {
  if (_prefsState._loaded) return _prefsState;
  if (_prefsState._loading) return _prefsState._loading;
  _prefsState._loading = (async () => {
    // Migração one-shot: empurra dados antigos do localStorage para o backend
    try {
      if (!localStorage.getItem(LS_PREFS_MIGRATED)) {
        const oldRS = readLS(LS_RECENT_SEARCH, []);
        const oldR = readLS(LS_RECENT, []);
        const oldF = readLS(LS_FAVS, []);
        if (oldRS.length || oldR.length || oldF.length) {
          await api.post('/dnacore/v3/prefs/migrate', { recent_searches: oldRS, recent_students: oldR, favorites: oldF });
        }
        localStorage.setItem(LS_PREFS_MIGRATED, '1');
      }
    } catch { /* migração silenciosa */ }

    try {
      const r = await api.get('/dnacore/v3/prefs');
      _prefsState.recent_searches = r.data.recent_searches || [];
      _prefsState.recent_students = r.data.recent_students || [];
      _prefsState.favorites = r.data.favorites || [];
    } catch {
      // offline fallback: cache local
      const cached = readLS(LS_PREFS_CACHE, null);
      if (cached) {
        _prefsState.recent_searches = cached.recent_searches || [];
        _prefsState.recent_students = cached.recent_students || [];
        _prefsState.favorites = cached.favorites || [];
      } else {
        // último fallback: legacy LS (caso o /migrate tenha falhado)
        _prefsState.recent_searches = readLS(LS_RECENT_SEARCH, []);
        _prefsState.recent_students = readLS(LS_RECENT, []);
        _prefsState.favorites = readLS(LS_FAVS, []);
      }
    }
    _prefsState._loaded = true;
    _persistCache();
    _notifyPrefs();
    return _prefsState;
  })();
  return _prefsState._loading;
}

function usePrefs() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const fn = () => setTick((x) => x + 1);
    _prefsListeners.add(fn);
    if (!_prefsState._loaded) loadPrefs();
    return () => _prefsListeners.delete(fn);
  }, []);
  return _prefsState;
}

async function pushSearchTerm(q) {
  q = (q || '').trim();
  if (!q) return;
  _prefsState.recent_searches = [q, ..._prefsState.recent_searches.filter((x) => x !== q)].slice(0, 12);
  _persistCache();
  _notifyPrefs();
  try { await api.post('/dnacore/v3/prefs/search', { q }); } catch { /* tudo bem, fica local */ }
}

async function pushRecentStudent(item) {
  if (!item?.id) return;
  const next = { ...item, ts: new Date().toISOString() };
  _prefsState.recent_students = [next, ..._prefsState.recent_students.filter((x) => x.id !== item.id)].slice(0, 12);
  _persistCache();
  _notifyPrefs();
  try { await api.post('/dnacore/v3/prefs/recent', { id: item.id, type: item.type || 'student', label: item.label, sub: item.sub }); } catch { /* offline ok */ }
}

async function toggleFavoriteApi(item) {
  if (!item?.id) return false;
  const t = item.type || 'student';
  const idx = _prefsState.favorites.findIndex((x) => x.id === item.id && (x.type || 'student') === t);
  let added;
  if (idx >= 0) { _prefsState.favorites.splice(idx, 1); added = false; }
  else { _prefsState.favorites = [{ ...item, type: t, ts: new Date().toISOString() }, ..._prefsState.favorites].slice(0, 60); added = true; }
  _persistCache();
  _notifyPrefs();
  try { await api.post('/dnacore/v3/prefs/favorite', { id: item.id, type: t, label: item.label, sub: item.sub }); } catch { /* offline ok */ }
  return added;
}

function isFavoriteApi(item) {
  if (!item?.id) return false;
  const t = item.type || 'student';
  return _prefsState.favorites.some((x) => x.id === item.id && (x.type || 'student') === t);
}

// ── Legacy helpers (mantidos por compat com chamadas residuais — agora delegam para o store backend) ──
const pushRecent = (item) => { pushRecentStudent(item); };
const toggleFavorite = (item) => {
  const t = item.type || 'student';
  const idx = _prefsState.favorites.findIndex((x) => x.id === item.id && (x.type || 'student') === t);
  const added = idx < 0;
  toggleFavoriteApi(item);
  return added;
};
const isFavorite = (item) => isFavoriteApi(item);

// Estilo dos botões de ação do Aluno 360 V3.2
const qaBtnStyle = (color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6,
  fontSize: 12, padding: '7px 12px', borderRadius: 10,
  background: color + '18', color, border: `1px solid ${color}40`,
  cursor: 'pointer', fontWeight: 600,
});

// ────────────────────────────────────  RADAR (Sidebar V3)  ────────────────────────────────────
function RadarMini({ data, navigate }) {
  const items = [
    { key: 'academico', label: 'Acadêmico', value: data?.academico ?? '—', unit: '%', threshold: { ok: 90, warn: 70 }, path: '/v2/academico' },
    { key: 'financeiro', label: 'Financeiro', value: data?.financeiro ?? '—', unit: '%', threshold: { ok: 90, warn: 70 }, path: '/v2/financeiro' },
    { key: 'cobranca', label: 'Cobrança', value: data?.cobranca ?? '—', unit: '%', threshold: { ok: 90, warn: 70 }, path: '/v2/financeiro' },
    { key: 'frequencia', label: 'Frequência', value: data?.frequencia ?? '—', unit: '%', threshold: { ok: 90, warn: 70 }, path: '/v2/academico' },
    { key: 'pendencias', label: 'Pendências', value: data?.pendencias ?? '—', unit: '', threshold: { ok: 5, warn: 30, inverse: true }, path: '/v2/ia' },
  ];
  const dotFor = (v, th) => {
    if (v === '—' || v == null) return '#475569';
    const n = Number(v);
    if (th.inverse) return n <= th.ok ? '#22c55e' : n <= th.warn ? '#f59e0b' : '#ef4444';
    return n >= th.ok ? '#22c55e' : n >= th.warn ? '#f59e0b' : '#ef4444';
  };
  return (
    <div className="dnav3-radar">
      {items.map((it) => (
        <div
          key={it.key}
          className="row dnav3-radar-row"
          onClick={() => navigate && navigate(it.path)}
          data-testid={`dnav3-radar-${it.key}`}
          style={{ cursor: navigate ? 'pointer' : 'default' }}
        >
          <span className="dot" style={{ background: dotFor(it.value, it.threshold) }} />
          <span className="label">{it.label}</span>
          <span className="value">{it.value}{it.unit}</span>
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────  COMMAND PALETTE (⌘K)  ────────────────────────────────────
const KNOWN_COMMANDS = [
  { kw: ['aluno', 'alunos'], label: 'Abrir Alunos', path: '/v2/alunos' },
  { kw: ['financeiro', 'dnapay', 'cobranca', 'cobrança'], label: 'Abrir DNAPay / Financeiro', path: '/v2/financeiro' },
  { kw: ['turma', 'turmas', 'classes'], label: 'Abrir Turmas', path: '/v2/academico' },
  { kw: ['recuperacao', 'recuperação'], label: 'Abrir Recuperação', path: '/v2/academico' },
  { kw: ['evento', 'eventos', 'festa'], label: 'Abrir Eventos', path: '/v2/eventos' },
  { kw: ['ia', 'pendencias', 'pendências'], label: 'Abrir IA Pendências', path: '/v2/ia' },
  { kw: ['notas', 'central'], label: 'Abrir Central de Notas', path: '/v2/academico' },
  { kw: ['comunicacao', 'comunicação', 'comunicacoes', 'comunicados'], label: 'Abrir Comunicações', path: '/v2/comunicacoes' },
  { kw: ['arena', 'batalha', 'campeoes', 'campeões', 'desafio'], label: 'Abrir Desafios dos Campeões', path: '/v2/ia/quiz' },
  { kw: ['home', 'painel', 'inicio', 'início'], label: 'Abrir Home V3', path: '/v3' },
  { kw: ['admin', 'configuracoes', 'configurações'], label: 'Abrir Sistema', path: '/admin' },
];

function parseAbrirCommand(query) {
  const q = (query || '').toLowerCase().trim();
  if (!q.startsWith('abrir ')) return null;
  const term = q.replace(/^abrir\s+/, '').trim();
  if (!term) return null;
  // turma 7A → match Turmas
  const cmd = KNOWN_COMMANDS.find((c) => c.kw.some((k) => term.startsWith(k)));
  if (cmd) return { ...cmd, term };
  // turma <nome>
  if (/^turma\s+/.test(term)) return { label: `Abrir turma ${term.replace(/^turma\s+/, '').toUpperCase()}`, path: '/v2/academico' };
  return null;
}

function CommandPalette({ open, onClose, onPickStudent }) {
  const navigate = useNavigate();
  const prefs = usePrefs();
  const [q, setQ] = useState('');
  const [groups, setGroups] = useState([]);
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const cacheRef = useRef(new Map()); // q -> { ts, data }
  const cmd = useMemo(() => parseAbrirCommand(q), [q]);

  useEffect(() => {
    if (open) {
      setQ(''); setItems([]); setGroups([]); setActive(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (!term || term.toLowerCase().startsWith('abrir ')) {
      setItems([]); setGroups([]); return;
    }
    // cache 30s
    const cached = cacheRef.current.get(term);
    const now = Date.now();
    if (cached && (now - cached.ts) < 30000) {
      setItems(cached.data.items); setGroups(cached.data.groups);
      return;
    }
    setLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get(`/dnacore/v3/spotlight?q=${encodeURIComponent(term)}&limit=8`);
        const data = { items: r.data.items || [], groups: r.data.groups || [] };
        cacheRef.current.set(term, { ts: now, data });
        setItems(data.items); setGroups(data.groups); setActive(0);
      } catch { /* noop */ } finally { setLoading(false); }
    }, 180);
    return () => clearTimeout(debounceRef.current);
  }, [q, open]);

  const handlePick = (it) => {
    // V3.2 — registra recente e busca (agora persistido no backend)
    if (it.type === 'student' || it.type === 'responsible') {
      if (it.id) {
        pushRecentStudent({ id: it.id, type: 'student', label: it.label, sub: it.sub });
        onPickStudent(it.id);
      }
    } else if (it.type === 'class') {
      navigate(`/v2/academico`);
    } else if (it.type === 'teacher') {
      navigate(`/v2/secretaria`);
    } else if (it.type === 'event') {
      navigate(`/v2/eventos`);
    } else if (it.type === 'invoice' || it.type === 'contract') {
      navigate(`/v2/financeiro`);
    }
    if (q.trim()) {
      pushSearchTerm(q.trim());
    }
    onClose();
  };

  const onKey = (e) => {
    if (e.key === 'Escape') { onClose(); return; }
    const list = cmd ? [cmd] : items;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(list.length - 1, a + 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    if (e.key === 'Enter') {
      if (cmd) { navigate(cmd.path); onClose(); return; }
      if (items[active]) handlePick(items[active]);
    }
  };

  if (!open) return null;
  return (
    <div className="dnav3-cmd-overlay" onClick={onClose} data-testid="dnav3-cmd-overlay">
      <div className="dnav3-cmd" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 16px 0' }}>
          <Search className="w-4 h-4" style={{ color: '#8a93a6' }} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder='Pesquisar tudo… ou "abrir financeiro", "abrir turma 7A"'
            data-testid="dnav3-cmd-input"
          />
          <span className="kbd"><Command className="w-3 h-3" />K</span>
          <button title="Atalhos (Ctrl+/)" onClick={() => window.__dnaOpenCheatsheet && window.__dnaOpenCheatsheet()} style={{ background: 'none', border: 'none', color: '#a5b4fc', cursor: 'pointer', fontSize: 11, fontWeight: 700 }} data-testid="dnav3-cmd-help">?</button>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#8a93a6', cursor: 'pointer' }}><X className="w-4 h-4" /></button>
        </div>
        <div className="results" data-testid="dnav3-cmd-results">
          {cmd && (
            <div className="item active" onClick={() => { navigate(cmd.path); onClose(); }} data-testid="dnav3-cmd-command">
              <div className="ph"><Sparkles className="w-4 h-4" /></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700 }}>{cmd.label}</div>
                <div style={{ fontSize: 11.5, color: '#8a93a6' }}>Comando · ↵ executar</div>
              </div>
              <span style={{ fontSize: 10, color: '#8a93a6' }}>{cmd.path}</span>
            </div>
          )}
          {loading && <div style={{ padding: 16, color: '#8a93a6', fontSize: 13 }}>Pesquisando…</div>}
          {!loading && !cmd && q.trim() && items.length === 0 && (
            <div style={{ padding: 24, color: '#8a93a6', fontSize: 13, textAlign: 'center' }} data-testid="dnav3-cmd-empty-search">
              <div style={{ fontSize: 28, marginBottom: 6 }}>🔎</div>
              <div style={{ fontWeight: 600, color: '#cbd5e1', marginBottom: 4 }}>Nenhum resultado para &quot;{q}&quot;</div>
              <div style={{ fontSize: 12 }}>Tente buscar por nome, CPF, telefone, turma, RA do aluno ou número de boleto/contrato.</div>
              <div style={{ marginTop: 10, fontSize: 11, color: '#a5b4fc' }}>Ou tente um comando: &quot;abrir financeiro&quot;, &quot;abrir turma 7A&quot;</div>
            </div>
          )}
          {!loading && !cmd && !q.trim() && (
            <div data-testid="dnav3-cmd-empty">
              {(() => {
                const favs = prefs.favorites;
                const recs = prefs.recent_students;
                const recSearches = prefs.recent_searches;
                if (favs.length === 0 && recs.length === 0 && recSearches.length === 0) {
                  return (
                    <div style={{ padding: 16, color: '#8a93a6', fontSize: 13 }}>
                      Digite para buscar · ou tente &quot;abrir financeiro&quot;, &quot;abrir turma 7A&quot;.
                      <div style={{ marginTop: 8, fontSize: 11 }}>Atalhos: ↑↓ navegar · ↵ abrir · Esc fechar</div>
                    </div>
                  );
                }
                return (
                  <div>
                    {favs.length > 0 && (
                      <>
                        <div style={{ padding: '8px 16px 4px', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: '#fbbf24', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Star className="w-3 h-3" /> Favoritos
                        </div>
                        {favs.slice(0, 5).map((it) => (
                          <div key={`f-${it.id}`} className="item" onClick={() => handlePick(it)} data-testid={`dnav3-fav-${it.id}`}>
                            <div className="ph"><Star className="w-3.5 h-3.5" /></div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 700 }}>{it.label}</div>
                              <div style={{ fontSize: 11.5, color: '#8a93a6' }}>{it.sub || it.type}</div>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                    {recs.length > 0 && (
                      <>
                        <div style={{ padding: '8px 16px 4px', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: '#8a93a6', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Clock className="w-3 h-3" /> Recentes
                        </div>
                        {recs.slice(0, 5).map((it) => (
                          <div key={`r-${it.id}`} className="item" onClick={() => handlePick(it)} data-testid={`dnav3-recent-${it.id}`}>
                            <div className="ph">{(it.label || '?').charAt(0).toUpperCase()}</div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 700 }}>{it.label}</div>
                              <div style={{ fontSize: 11.5, color: '#8a93a6' }}>{it.sub}</div>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                    {recSearches.length > 0 && (
                      <>
                        <div style={{ padding: '8px 16px 4px', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: '#8a93a6' }}>Últimas buscas</div>
                        <div style={{ padding: '4px 16px 12px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {recSearches.slice(0, 8).map((s) => (
                            <button key={s} onClick={() => setQ(s)} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 999, background: 'rgba(255,255,255,0.06)', color: '#cbd5e1', border: '1px solid rgba(255,255,255,0.10)', cursor: 'pointer' }}>{s}</button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
          {!cmd && groups.map((g) => (
            <div key={g.key}>
              <div style={{ padding: '8px 16px 4px', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: '#8a93a6' }}>{g.label}</div>
              {g.items.map((it, i) => {
                const globalIdx = items.findIndex((x) => x === it);
                return (
                  <div
                    key={`${it.type}-${it.id || it.responsible_id || i}`}
                    className={`item ${globalIdx === active ? 'active' : ''}`}
                    onClick={() => handlePick(it)}
                    onMouseEnter={() => setActive(globalIdx)}
                    data-testid={`dnav3-cmd-item-${globalIdx}`}
                  >
                    <div className="ph">{(it.label || '?').charAt(0).toUpperCase()}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700 }}>{it.label}</div>
                      <div style={{ fontSize: 11.5, color: '#8a93a6' }}>{it.sub}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────  CHEATSHEET (Ctrl+/) V3.3  ────────────────────────────────────
function Cheatsheet({ open, onClose }) {
  if (!open) return null;
  const shortcuts = [
    { label: 'Busca Global', keys: ['⌘', 'K'] },
    { label: 'Aluno 360 — abrir Spotlight', keys: ['⌘', 'Space'] },
    { label: 'Ações Rápidas', keys: ['⌘', '⇧', 'A'] },
    { label: 'Cheatsheet (esta tela)', keys: ['⌘', '/'] },
    { label: 'Fechar overlay', keys: ['Esc'] },
    { label: 'Navegar resultados', keys: ['↑', '↓'] },
    { label: 'Abrir resultado', keys: ['↵'] },
  ];
  const commands = [
    'abrir financeiro', 'abrir DNAPay', 'abrir alunos', 'abrir turma 7A',
    'abrir notas', 'abrir recuperação', 'abrir eventos', 'abrir IA',
    'abrir comunicação', 'abrir batalha', 'abrir home',
  ];
  return (
    <div className="dnav3-cheatsheet-overlay" onClick={onClose} data-testid="dnav3-cheatsheet-overlay">
      <div className="dnav3-cheatsheet" onClick={(e) => e.stopPropagation()}>
        <h3>Atalhos do DNA Command Center</h3>
        <div className="sub">Acelere a operação com o teclado · pressione <strong style={{ color: '#cbd5e1' }}>Esc</strong> para fechar</div>
        {shortcuts.map((s) => (
          <div className="row" key={s.label}>
            <span className="label">{s.label}</span>
            <span className="keys">
              {s.keys.map((k, i) => <span key={i}>{k}</span>)}
            </span>
          </div>
        ))}
        <div style={{ marginTop: 16, fontSize: 10, color: 'var(--dna-fg-dim)', letterSpacing: 2, textTransform: 'uppercase' }}>Comandos no Spotlight</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {commands.map((c) => (
            <span key={c} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 999, background: 'rgba(99,102,241,0.10)', color: '#a5b4fc', border: '1px solid rgba(99,102,241,0.25)' }}>{c}</span>
          ))}
        </div>
        <div style={{ marginTop: 14, fontSize: 11, color: 'var(--dna-fg-dim)', textAlign: 'center' }}>
          Dica: digite o início e pressione ↵ — vai direto pro módulo.
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────  NEURAL BACKGROUND CANVAS V3.3  ────────────────────────────────────
function NeuralBackground() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let raf = 0;
    let nodes = [];
    let mounted = true;

    const resize = () => {
      canvas.width = window.innerWidth * window.devicePixelRatio;
      canvas.height = window.innerHeight * window.devicePixelRatio;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
      const count = Math.min(38, Math.floor((window.innerWidth * window.innerHeight) / 38000));
      nodes = Array.from({ length: count }).map(() => ({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        vx: (Math.random() - 0.5) * 0.18,
        vy: (Math.random() - 0.5) * 0.18,
      }));
    };
    resize();
    window.addEventListener('resize', resize);

    const tick = () => {
      if (!mounted) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // move nodes
      nodes.forEach((n) => {
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0 || n.x > window.innerWidth) n.vx *= -1;
        if (n.y < 0 || n.y > window.innerHeight) n.vy *= -1;
      });
      // draw connections
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const dist = Math.hypot(dx, dy);
          if (dist < 140) {
            const alpha = (1 - dist / 140) * 0.18;
            ctx.strokeStyle = `rgba(255, 59, 107, ${alpha})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
      // draw nodes
      ctx.fillStyle = 'rgba(99, 102, 241, 0.45)';
      nodes.forEach((n) => {
        ctx.beginPath();
        ctx.arc(n.x, n.y, 1.4, 0, Math.PI * 2);
        ctx.fill();
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      mounted = false;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);
  return <canvas ref={canvasRef} className="dnav3-neural-bg" data-testid="dnav3-neural-bg" />;
}

// ────────────────────────────────────  QUICK ACTIONS DRAWER  ────────────────────────────────────
function QuickActionsDrawer({ open, onClose }) {
  const navigate = useNavigate();
  if (!open) return null;
  const actions = [
    { label: 'Novo aluno', icon: UserPlus, color: '#22c55e', path: '/v2/alunos' },
    { label: 'Nova cobrança', icon: Receipt, color: '#f59e0b', path: '/v2/financeiro' },
    { label: 'Nova venda', icon: DollarSign, color: '#fb7185', path: '/v2/festa-junina' },
    { label: 'Novo comunicado', icon: MessageCircle, color: '#6366f1', path: '/v2/comunicacoes' },
    { label: 'Nova recuperação', icon: AlertTriangle, color: '#f97316', path: '/v2/academico' },
    { label: 'Nova batalha', icon: Sparkles, color: '#ef4444', path: '/v2/ia/quiz' },
    { label: 'Novo evento', icon: Calendar, color: '#06b6d4', path: '/v2/eventos' },
    { label: 'Nova ocorrência', icon: ClipboardList, color: '#a78bfa', path: '/v2/pedagogico' },
  ];
  return (
    <div className="dnav3-360-overlay" onClick={onClose} data-testid="dnav3-quick-overlay">
      <div className="dnav3-360" style={{ width: 'min(420px, 100vw)' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: '#8a93a6' }}>V3.2</div>
            <div style={{ fontSize: 24, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Zap className="w-6 h-6" style={{ color: '#fbbf24' }} /> Ações Rápidas
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, padding: '6px 10px', color: '#e6e8ee', cursor: 'pointer' }}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <div style={{ marginTop: 18, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {actions.map((a) => {
            const Icon = a.icon;
            return (
              <button
                key={a.label}
                onClick={() => { navigate(a.path); onClose(); }}
                data-testid={`dnav3-qa-${a.label.replace(/\s+/g, '-').toLowerCase()}`}
                style={{
                  display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start',
                  padding: '16px 14px', borderRadius: 14, cursor: 'pointer',
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.10)',
                  color: '#fff', fontWeight: 600, fontSize: 13, textAlign: 'left',
                  transition: 'transform 0.14s, background 0.14s, border-color 0.14s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor = a.color; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)'; e.currentTarget.style.transform = 'none'; }}
              >
                <span style={{ width: 36, height: 36, borderRadius: 10, background: a.color + '20', border: `1px solid ${a.color}40`, display: 'grid', placeItems: 'center' }}>
                  <Icon className="w-4 h-4" style={{ color: a.color }} />
                </span>
                {a.label}
              </button>
            );
          })}
        </div>
        <div style={{ marginTop: 14, fontSize: 11, color: '#8a93a6', textAlign: 'center' }}>
          Atalho: ⌘ / Ctrl + Shift + A
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────  ALUNO 360 OVERLAY (Premium V3.1)  ────────────────────────────────────
function Aluno360Overlay({ studentId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('resumo');
  const [tabData, setTabData] = useState({}); // { financeiro: {...}, comunicacoes: {...}, ... } — lazy-cache
  const [tabLoading, setTabLoading] = useState({}); // { financeiro: true, ... }
  const prefs = usePrefs();

  useEffect(() => {
    if (!studentId) return;
    setData(null); setError(null); setTab('resumo'); setTabData({}); setTabLoading({});
    // Lazy V3.x — carrega apenas o resumo + acadêmico (lite=true). Abas pesadas vêm sob demanda.
    api.get(`/dnacore/v3/student/${studentId}?lite=true`)
      .then((r) => setData(r.data))
      .catch(() => setError('Falha ao carregar.'));
  }, [studentId]);

  // Carrega dados de aba pesada sob demanda (lazy-load real V3.x)
  const ensureTab = useCallback(async (tabId) => {
    const heavyTabs = ['financeiro', 'comunicacoes', 'recuperacao', 'ocorrencias', 'conquistas', 'documentos', 'timeline'];
    if (!heavyTabs.includes(tabId)) return;
    if (tabData[tabId] || tabLoading[tabId]) return;
    setTabLoading((s) => ({ ...s, [tabId]: true }));
    try {
      // Conquistas usa rota diferente (/champions/students/{id}/trophies)
      const url = tabId === 'conquistas'
        ? `/champions/students/${studentId}/trophies`
        : `/dnacore/v3/student/${studentId}/tab/${tabId}`;
      const r = await api.get(url);
      setTabData((s) => ({ ...s, [tabId]: r.data }));
    } catch {
      setTabData((s) => ({ ...s, [tabId]: { _error: true } }));
    } finally {
      setTabLoading((s) => ({ ...s, [tabId]: false }));
    }
  }, [studentId, tabData, tabLoading]);

  const switchTab = useCallback((id) => {
    setTab(id);
    ensureTab(id);
  }, [ensureTab]);

  // Mobile V4 — swipe entre abas
  const TAB_IDS_REF = useRef(null);
  const touchStartRef = useRef(null);
  const onTouchStart = (e) => {
    if (!e.touches?.[0]) return;
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const onTouchEnd = (e) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start || !e.changedTouches?.[0]) return;
    const dx = e.changedTouches[0].clientX - start.x;
    const dy = e.changedTouches[0].clientY - start.y;
    if (Math.abs(dx) < 60 || Math.abs(dy) > 60) return; // gesto horizontal claro
    const ids = TAB_IDS_REF.current || [];
    const idx = ids.indexOf(tab);
    if (idx < 0) return;
    if (dx < 0 && idx < ids.length - 1) switchTab(ids[idx + 1]);
    else if (dx > 0 && idx > 0) switchTab(ids[idx - 1]);
  };

  if (!studentId) return null;
  const s = data?.student || {};
  const r = data?.responsible || {};
  const phone = r.phone || r.cellphone || r.whatsapp || '';
  const initials = (s.name || '?').split(' ').slice(0, 2).map((x) => x[0]).join('').toUpperCase();

  const TABS = [
    { id: 'resumo', label: 'Resumo' },
    { id: 'academico', label: 'Acadêmico' },
    { id: 'financeiro', label: 'Financeiro' },
    { id: 'comunicacoes', label: 'Comunicações' },
    { id: 'recuperacao', label: 'Recuperação' },
    { id: 'ocorrencias', label: 'Ocorrências' },
    { id: 'conquistas', label: 'Conquistas' },
    { id: 'documentos', label: 'Documentos' },
    { id: 'timeline', label: 'Timeline' },
  ];
  TAB_IDS_REF.current = TABS.map((t) => t.id);

  const fmtDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleString('pt-BR'); } catch { return String(d); }
  };

  return (
    <div className="dnav3-360-overlay" onClick={onClose} data-testid="dnav3-360-overlay">
      <div className="dnav3-360" onClick={(e) => e.stopPropagation()} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, padding: '6px 10px', color: '#e6e8ee', cursor: 'pointer' }} data-testid="dnav3-360-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!data && !error && (
          <div style={{ display: 'flex', gap: 12 }}>
            <div className="dnav3-skel" style={{ width: 72, height: 72 }} />
            <div style={{ flex: 1 }}>
              <div className="dnav3-skel" style={{ height: 24, marginBottom: 8 }} />
              <div className="dnav3-skel" style={{ height: 14, width: '60%' }} />
            </div>
          </div>
        )}
        {error && <div style={{ color: '#ef4444' }}>{error}</div>}

        {data && (
          <>
            <div className="hero">
              {s.photo_url ? (
                <img src={s.photo_url} alt={s.name} className="avatar" style={{ objectFit: 'cover' }} />
              ) : (
                <div className="avatar">{initials}</div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, color: '#8a93a6' }}>Aluno 360</div>
                <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1 }}>{s.name}</div>
                <div style={{ color: '#8a93a6', fontSize: 13, marginTop: 4 }}>
                  {[s.class_name, s.ra && `RA ${s.ra}`, s.cpf].filter(Boolean).join(' · ')}
                </div>
              </div>
              <button
                onClick={() => {
                  const fav = { id: s.id, type: 'student', label: s.name, sub: s.class_name };
                  toggleFavoriteApi(fav);
                  setData({ ...data, _fav: isFavoriteApi(fav) });
                }}
                title="Favoritar"
                data-testid="dnav3-360-fav"
                style={{
                  background: isFavoriteApi({ id: s.id, type: 'student' }) ? 'rgba(251,191,36,0.18)' : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${isFavoriteApi({ id: s.id, type: 'student' }) ? '#fbbf24' : 'rgba(255,255,255,0.10)'}`,
                  borderRadius: 12, padding: 10, color: isFavoriteApi({ id: s.id, type: 'student' }) ? '#fbbf24' : '#cbd5e1', cursor: 'pointer',
                }}
              >
                <Star className="w-4 h-4" />
              </button>
            </div>

            {/* V3.2 — Ações diretas do Aluno 360 */}
            <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }} data-testid="dnav3-360-actions">
              {phone && (
                <a href={`https://wa.me/55${(phone || '').replace(/\D/g, '')}`} target="_blank" rel="noreferrer"
                   style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '7px 12px', borderRadius: 10, background: 'rgba(34,197,94,0.12)', color: '#86efac', border: '1px solid rgba(34,197,94,0.3)', textDecoration: 'none' }}>
                  <Phone className="w-3.5 h-3.5" /> WhatsApp
                </a>
              )}
              {r.email && (
                <a href={`mailto:${r.email}`}
                   style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '7px 12px', borderRadius: 10, background: 'rgba(99,102,241,0.12)', color: '#a5b4fc', border: '1px solid rgba(99,102,241,0.3)', textDecoration: 'none' }}>
                  <Mail className="w-3.5 h-3.5" /> E-mail
                </a>
              )}
              <button onClick={() => { window.location.href = '/v2/financeiro'; }} style={qaBtnStyle('#fbbf24')} data-testid="dnav3-360-act-fin">
                <CreditCard className="w-3.5 h-3.5" /> Financeiro
              </button>
              <button onClick={() => { window.location.href = '/v2/academico'; }} style={qaBtnStyle('#06b6d4')} data-testid="dnav3-360-act-notas">
                <BookOpen className="w-3.5 h-3.5" /> Notas
              </button>
              <button onClick={() => { window.location.href = '/v2/academico'; }} style={qaBtnStyle('#a5b4fc')} data-testid="dnav3-360-act-freq">
                <PieChart className="w-3.5 h-3.5" /> Frequência
              </button>
              <button onClick={() => { window.location.href = '/v2/pedagogico'; }} style={qaBtnStyle('#fb7185')} data-testid="dnav3-360-act-ocor">
                <ClipboardList className="w-3.5 h-3.5" /> Ocorrência
              </button>
              <button onClick={() => { window.location.href = '/v2/academico'; }} style={qaBtnStyle('#f97316')} data-testid="dnav3-360-act-rec">
                <AlertTriangle className="w-3.5 h-3.5" /> Recuperação
              </button>
              <button onClick={() => window.open(`/v4/audit/${studentId}`, '_blank', 'noopener')} style={qaBtnStyle('#22d3ee')} data-testid="dnav3-360-act-investigar">
                <Search className="w-3.5 h-3.5" /> Investigar Dados
              </button>
            </div>

            {/* Abas premium */}
            <div style={{ display: 'flex', gap: 4, marginTop: 16, borderBottom: '1px solid rgba(255,255,255,0.08)', overflowX: 'auto' }}>
              {TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => switchTab(t.id)}
                  data-testid={`dnav3-360-tab-${t.id}`}
                  style={{
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    padding: '10px 14px', fontSize: 13, fontWeight: 600,
                    color: tab === t.id ? '#fff' : '#8a93a6',
                    borderBottom: `2px solid ${tab === t.id ? '#ff3b6b' : 'transparent'}`,
                    transition: 'color 0.15s, border-color 0.15s',
                  }}
                >{t.label}</button>
              ))}
            </div>

            <div style={{ marginTop: 16 }}>
              {tab === 'resumo' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="stat">
                    <div className="lbl">Média</div>
                    <div className="val">{data.notas?.media ?? '—'}</div>
                    <div style={{ color: '#8a93a6', fontSize: 11, marginTop: 4 }}>
                      {data.notas?.lancadas || 0} lançadas · {data.notas?.pendentes || 0} pendentes
                    </div>
                  </div>
                  <div className="stat">
                    <div className="lbl">Frequência</div>
                    <div className="val">{data.frequencia_pct != null ? `${data.frequencia_pct}%` : '—'}</div>
                    <div style={{ color: '#8a93a6', fontSize: 11, marginTop: 4 }}>Últimos 30 dias</div>
                  </div>
                  <div className="stat">
                    <div className="lbl">Financeiro Aberto</div>
                    <div className="val" style={{ color: '#fbbf24' }}>R$ {(data.financeiro?.valor_aberto || 0).toFixed(2)}</div>
                    <div style={{ color: '#8a93a6', fontSize: 11, marginTop: 4 }}>{data.financeiro?.abertas || 0} faturas em aberto</div>
                  </div>
                  <div className="stat">
                    <div className="lbl">Vencido</div>
                    <div className="val" style={{ color: data.financeiro?.vencidas ? '#ef4444' : '#22c55e' }}>R$ {(data.financeiro?.valor_vencido || 0).toFixed(2)}</div>
                    <div style={{ color: '#8a93a6', fontSize: 11, marginTop: 4 }}>{data.financeiro?.vencidas || 0} faturas vencidas</div>
                  </div>
                  <div className="stat" style={{ gridColumn: '1 / -1' }}>
                    <div className="lbl">Responsável</div>
                    <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>{r.name || '—'}</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                      {phone && (
                        <a href={`https://wa.me/55${(phone || '').replace(/\D/g, '')}`} target="_blank" rel="noreferrer"
                           style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '6px 10px', borderRadius: 10, background: 'rgba(34,197,94,0.12)', color: '#86efac', border: '1px solid rgba(34,197,94,0.3)' }}>
                          <Phone className="w-3.5 h-3.5" /> {phone}
                        </a>
                      )}
                      {r.email && (
                        <a href={`mailto:${r.email}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '6px 10px', borderRadius: 10, background: 'rgba(99,102,241,0.12)', color: '#a5b4fc', border: '1px solid rgba(99,102,241,0.3)' }}>
                          <Mail className="w-3.5 h-3.5" /> {r.email}
                        </a>
                      )}
                    </div>
                    {data.last_access_familia && (
                      <div style={{ fontSize: 11, color: '#8a93a6', marginTop: 6 }}>Último acesso ao DNA Connect: {fmtDate(data.last_access_familia)}</div>
                    )}
                  </div>
                </div>
              )}

              {tab === 'academico' && (
                <div className="stat">
                  <div className="lbl">Boletim resumido</div>
                  <div className="val">{data.notas?.media ?? '—'}</div>
                  <div style={{ marginTop: 8, color: '#cbd5e1', fontSize: 13 }}>
                    {data.notas?.lancadas || 0} avaliações lançadas · {data.notas?.pendentes || 0} pendentes
                  </div>
                  <div style={{ marginTop: 8, color: '#8a93a6', fontSize: 12 }}>Frequência média (30d): {data.frequencia_pct != null ? `${data.frequencia_pct}%` : '—'}</div>
                </div>
              )}

              {tab === 'financeiro' && (() => {
                const td = tabData.financeiro;
                if (tabLoading.financeiro || !td) {
                  return <div className="stat"><div className="dnav3-skel" style={{ height: 60 }} /></div>;
                }
                const fin = td.financeiro || {};
                return (
                  <div className="stat">
                    <div className="lbl">Última cobrança</div>
                    {td.last_cobranca ? (
                      <>
                        <div className="val" style={{ color: '#fbbf24' }}>R$ {Number(td.last_cobranca.amount).toFixed(2)}</div>
                        <div style={{ fontSize: 12, color: '#8a93a6' }}>{fmtDate(td.last_cobranca.date)} · status: {td.last_cobranca.status || '—'}</div>
                      </>
                    ) : <div style={{ color: '#8a93a6' }}>Nenhuma cobrança registrada.</div>}
                    <div style={{ marginTop: 14, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 12 }}>
                      <div className="lbl">Em aberto · Vencido</div>
                      <div style={{ display: 'flex', gap: 14, marginTop: 6 }}>
                        <div><strong style={{ color: '#fbbf24' }}>R$ {(fin.valor_aberto || 0).toFixed(2)}</strong> <span style={{ color: '#8a93a6' }}>({fin.abertas || 0})</span></div>
                        <div><strong style={{ color: fin.vencidas ? '#ef4444' : '#22c55e' }}>R$ {(fin.valor_vencido || 0).toFixed(2)}</strong> <span style={{ color: '#8a93a6' }}>({fin.vencidas || 0})</span></div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {tab === 'comunicacoes' && (() => {
                const td = tabData.comunicacoes;
                if (tabLoading.comunicacoes || !td) {
                  return <div className="stat"><div className="dnav3-skel" style={{ height: 40 }} /></div>;
                }
                return (
                  <div className="stat">
                    <div className="lbl">Último comunicado</div>
                    {td.last_comunicado ? (
                      <>
                        <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>{td.last_comunicado.title}</div>
                        <div style={{ fontSize: 12, color: '#8a93a6' }}>{fmtDate(td.last_comunicado.date)}</div>
                      </>
                    ) : <div style={{ color: '#8a93a6' }}>Nenhuma comunicação registrada.</div>}
                  </div>
                );
              })()}

              {tab === 'recuperacao' && (() => {
                const td = tabData.recuperacao;
                if (tabLoading.recuperacao || !td) {
                  return <div className="stat"><div className="dnav3-skel" style={{ height: 40 }} /></div>;
                }
                const rec = td.recuperacao;
                return (
                  <div className="stat" style={{ background: rec ? 'rgba(245,158,11,0.10)' : 'rgba(34,197,94,0.10)', borderColor: rec ? 'rgba(245,158,11,0.4)' : 'rgba(34,197,94,0.3)' }}>
                    <div className="lbl" style={{ color: rec ? '#fbbf24' : '#86efac' }}>{rec ? '⚠ Em recuperação' : '✓ Sem recuperação'}</div>
                    {td.last_recuperacao ? (
                      <div style={{ fontSize: 13, marginTop: 6 }}>
                        Última nota de recuperação: {td.last_recuperacao.score} em {td.last_recuperacao.subject || '—'} · {fmtDate(td.last_recuperacao.updated_at)}
                      </div>
                    ) : <div style={{ color: '#8a93a6', fontSize: 12, marginTop: 6 }}>Nenhum lançamento de recuperação.</div>}
                  </div>
                );
              })()}

              {tab === 'ocorrencias' && (() => {
                const td = tabData.ocorrencias;
                if (tabLoading.ocorrencias || !td) {
                  return <div className="stat"><div className="dnav3-skel" style={{ height: 80 }} /></div>;
                }
                const list = td.ocorrencias || [];
                const sevColor = (s) => s === 'alta' || s === 'grave' ? '#ef4444' : s === 'media' || s === 'moderada' ? '#f59e0b' : '#22c55e';
                return (
                  <div className="stat">
                    <div className="lbl">Ocorrências ({td.total ?? list.length})</div>
                    {td.last_access_familia && (
                      <div style={{ fontSize: 11, color: '#8a93a6', marginTop: 4 }}>
                        Último acesso da família: {fmtDate(td.last_access_familia)}
                      </div>
                    )}
                    {list.length === 0 ? (
                      <div style={{ color: '#86efac', marginTop: 8 }}>✓ Sem ocorrências registradas.</div>
                    ) : (
                      <div style={{ marginTop: 10 }} data-testid="dnav3-360-ocorrencias-list">
                        {list.map((o, i) => (
                          <div key={o.id || i} style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ width: 8, height: 8, borderRadius: 999, background: sevColor(o.severity) }} />
                              <span style={{ fontWeight: 700, fontSize: 13 }}>{o.title || o.type || 'Ocorrência'}</span>
                              {o.status && <span style={{ marginLeft: 'auto', fontSize: 10, color: '#8a93a6', textTransform: 'uppercase', letterSpacing: 1 }}>{o.status}</span>}
                            </div>
                            {o.description && (
                              <div style={{ fontSize: 12, color: '#cbd5e1', marginTop: 4 }}>{o.description}</div>
                            )}
                            <div style={{ fontSize: 11, color: '#8a93a6', marginTop: 4 }}>
                              {[fmtDate(o.created_at), o.author_name].filter(Boolean).join(' · ')}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {tab === 'conquistas' && (() => {
                const td = tabData.conquistas;
                if (tabLoading.conquistas || !td) {
                  return <div className="stat"><div className="dnav3-skel" style={{ height: 100 }} /></div>;
                }
                const st = td.stats || {};
                const catalog = td.catalog || [];
                return (
                  <div data-testid="dnav3-360-conquistas">
                    {/* Stats lifetime */}
                    <div className="stat" style={{ marginBottom: 12 }}>
                      <div className="lbl">Arena DNA — estatística lifetime</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginTop: 8 }}>
                        <div style={{ textAlign: 'center', padding: 8, background: 'rgba(251,191,36,0.10)', borderRadius: 10, border: '1px solid rgba(251,191,36,0.25)' }}>
                          <div style={{ fontSize: 22, fontWeight: 800, color: '#fbbf24' }}>{st.wins ?? 0}</div>
                          <div style={{ fontSize: 10, color: '#8a93a6' }}>Vitórias</div>
                        </div>
                        <div style={{ textAlign: 'center', padding: 8, background: 'rgba(34,197,94,0.10)', borderRadius: 10, border: '1px solid rgba(34,197,94,0.25)' }}>
                          <div style={{ fontSize: 22, fontWeight: 800, color: '#22c55e' }}>{st.correct_answers ?? 0}</div>
                          <div style={{ fontSize: 10, color: '#8a93a6' }}>Acertos</div>
                        </div>
                        <div style={{ textAlign: 'center', padding: 8, background: 'rgba(6,182,212,0.10)', borderRadius: 10, border: '1px solid rgba(6,182,212,0.25)' }}>
                          <div style={{ fontSize: 22, fontWeight: 800, color: '#06b6d4' }}>{st.fast_answers ?? 0}</div>
                          <div style={{ fontSize: 10, color: '#8a93a6' }}>Rápidas</div>
                        </div>
                        <div style={{ textAlign: 'center', padding: 8, background: 'rgba(99,102,241,0.10)', borderRadius: 10, border: '1px solid rgba(99,102,241,0.25)' }}>
                          <div style={{ fontSize: 22, fontWeight: 800, color: '#a5b4fc' }}>{st.arenas_played ?? 0}</div>
                          <div style={{ fontSize: 10, color: '#8a93a6' }}>Arenas</div>
                        </div>
                      </div>
                    </div>
                    {/* Catálogo de troféus */}
                    <div className="stat">
                      <div className="lbl">Troféus</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8, marginTop: 10 }}>
                        {catalog.length === 0 && <div style={{ color: '#8a93a6', fontSize: 12 }}>Nenhum troféu disponível.</div>}
                        {catalog.map((t) => (
                          <div
                            key={t.code}
                            data-testid={`dnav3-trophy-${t.code}`}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                              borderRadius: 12,
                              background: t.unlocked ? `${t.color}1c` : 'rgba(255,255,255,0.03)',
                              border: `1px solid ${t.unlocked ? t.color + '60' : 'rgba(255,255,255,0.08)'}`,
                              opacity: t.unlocked ? 1 : 0.55,
                            }}
                          >
                            <div style={{ fontSize: 30, lineHeight: 1, filter: t.unlocked ? 'none' : 'grayscale(1)' }}>{t.icon}</div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 700, fontSize: 14, color: t.unlocked ? t.color : '#cbd5e1' }}>{t.title}</div>
                              <div style={{ fontSize: 11, color: '#8a93a6' }}>{t.description}</div>
                            </div>
                            {t.unlocked && (
                              <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 999, background: t.color + '30', color: t.color, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1 }}>
                                Conquistado
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {tab === 'documentos' && (() => {
                const td = tabData.documentos;
                if (tabLoading.documentos || !td) {
                  return <div className="stat"><div className="dnav3-skel" style={{ height: 60 }} /></div>;
                }
                const docs = td.documentos || [];
                return (
                  <div className="stat">
                    <div className="lbl">Documentos ({docs.length})</div>
                    {docs.length === 0 ? (
                      <div style={{ color: '#8a93a6', marginTop: 6 }}>Nenhum documento associado.</div>
                    ) : (
                      <div style={{ marginTop: 8 }}>
                        {docs.map((d) => (
                          <div key={d.id} style={{ padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.06)', fontSize: 13 }}>
                            {d.title} <span style={{ color: '#8a93a6', fontSize: 11 }}>· {d.type || '—'} · {fmtDate(d.created_at)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {tab === 'timeline' && (() => {
                const td = tabData.timeline;
                if (tabLoading.timeline || !td) {
                  return <div className="stat"><div className="dnav3-skel" style={{ height: 80 }} /></div>;
                }
                const tl = td.timeline || [];
                return (
                  <div className="stat">
                    <div className="lbl">Timeline</div>
                    {tl.length === 0 ? (
                      <div style={{ color: '#8a93a6', marginTop: 6 }}>Sem eventos registrados.</div>
                    ) : (
                      <div style={{ marginTop: 8 }}>
                        {tl.map((ev, i) => (
                          <div key={i} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                            <div style={{ width: 6, height: 6, borderRadius: 999, background: '#ff3b6b', marginTop: 6 }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13 }}>{ev.label}</div>
                              <div style={{ fontSize: 11, color: '#8a93a6' }}>{fmtDate(ev.date)}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────  FLOATING DOCK  ────────────────────────────────────
function FloatingDock({ onOpenSearch, onOpenStudent, onOpenAlerts, onOpenQuick, alertsCount }) {
  return (
    <div className="dnav3-dock" data-testid="dnav3-dock">
      <button title="Busca Global (⌘K)" onClick={onOpenSearch} data-testid="dnav3-dock-search"><Search className="w-5 h-5" /></button>
      <button title="Aluno 360" onClick={() => onOpenStudent()} data-testid="dnav3-dock-aluno"><GraduationCap className="w-5 h-5" /></button>
      <button title="Health Check (V4)" onClick={() => (window.location.href = '/v4/health')} data-testid="dnav3-dock-health" style={{ color: '#22c55e' }}><ShieldCheck className="w-5 h-5" /></button>
      <button title="Ações Rápidas (⌘⇧A)" onClick={onOpenQuick} data-testid="dnav3-dock-quick" style={{ color: '#fbbf24' }}><Zap className="w-5 h-5" /></button>
      <button title="Alertas" onClick={onOpenAlerts} data-testid="dnav3-dock-alerts" style={{ position: 'relative' }}>
        <Bell className="w-5 h-5" />
        {alertsCount > 0 && (
          <span style={{ position: 'absolute', top: 4, right: 4, background: '#ef4444', color: '#fff', borderRadius: 999, fontSize: 9, padding: '0 5px', fontWeight: 800 }}>{alertsCount}</span>
        )}
      </button>
      <button title="WhatsApp" onClick={() => window.open('https://web.whatsapp.com', '_blank')}><MessageCircle className="w-5 h-5" /></button>
      <button title="Email" onClick={() => window.open('https://mail.google.com', '_blank')}><Mail className="w-5 h-5" /></button>
      <button title="DNAPay" onClick={() => (window.location.href = '/v2/financeiro')}><CreditCard className="w-5 h-5" /></button>
    </div>
  );
}

// ────────────────────────────────────  ALUNO 360 FLOATING BIG BUTTON (left)  ────────────────────────────────────
function Aluno360FloatingButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid="dnav3-aluno360-fab"
      title="Aluno 360 — Ctrl+Space"
      className="dnav3-fab-aluno360"
    >
      <span className="helix-small" />
      <span className="lbl-text">Aluno 360</span>
      <span className="kbd-text">⌃ Space</span>
    </button>
  );
}

// ────────────────────────────────────  CENTRAL DE ALERTAS  ────────────────────────────────────
function AlertsOverlay({ open, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open) return;
    api.get('/dnacore/v3/alerts').then((r) => setData(r.data)).catch(() => setData({ total: 0, groups: [] }));
  }, [open]);
  if (!open) return null;
  return (
    <div className="dnav3-360-overlay" onClick={onClose} data-testid="dnav3-alerts-overlay">
      <div className="dnav3-360" style={{ width: 'min(480px, 100vw)' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: '#8a93a6' }}>Central</div>
            <div style={{ fontSize: 24, fontWeight: 800 }}>Alertas {data && <span style={{ fontSize: 14, color: '#8a93a6' }}>({data.total || 0})</span>}</div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, padding: '6px 10px', color: '#e6e8ee', cursor: 'pointer' }}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <div style={{ marginTop: 16 }}>
          {!data && <div className="dnav3-skel" style={{ height: 80 }} />}
          {data && data.total === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: '#86efac', border: '1px solid rgba(34,197,94,0.3)', background: 'rgba(34,197,94,0.05)', borderRadius: 14 }}>
              ✓ Nenhum alerta no momento. Tudo em ordem!
            </div>
          )}
          {data && data.groups.map((g) => (
            <div key={g.key} className="stat" style={{ marginBottom: 10 }}>
              <div className="lbl">{g.key}</div>
              {g.items.map((it, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 999, background: it.severity === 'bad' ? '#ef4444' : it.severity === 'warn' ? '#f59e0b' : '#22c55e' }} />
                  <span style={{ fontSize: 13 }}>{it.label}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════════
//   DNACore Mobile V4 — Premium mobile navigation (< 1024px)
//   Bottom Command Bar · Menu Mais fullscreen · QR FAB
// ════════════════════════════════════════════════════════════════════════════════
export function useIsMobile(breakpoint = 1024) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < breakpoint);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

function DnaMobileBottomBar({ active, onHome, onStudent, onQuick, onAlerts, onMore, alertsCount }) {
  return (
    <nav className="dna-mobilebar" data-testid="dna-mobilebar">
      <button className={`dna-mobilebar-btn ${active === 'home' ? 'active' : ''}`} onClick={onHome} data-testid="dna-mobilebar-home">
        <span style={{ fontSize: 20, lineHeight: 1 }}>🏠</span>
        <span>Home</span>
      </button>
      <button className={`dna-mobilebar-btn ${active === 'quick' ? 'active' : ''}`} onClick={onQuick} data-testid="dna-mobilebar-quick">
        <Zap className="w-5 h-5" style={{ color: '#fbbf24' }} />
        <span>Ações</span>
      </button>
      <button className="dna-mobilebar-center" onClick={onStudent} data-testid="dna-mobilebar-center" aria-label="Aluno 360">
        <div className="dna-mobilebar-orb">
          <span style={{ color: '#fff', fontWeight: 900, fontSize: 11, letterSpacing: 1 }}>360</span>
        </div>
        <span>Aluno</span>
      </button>
      <button className={`dna-mobilebar-btn ${active === 'alerts' ? 'active' : ''}`} onClick={onAlerts} data-testid="dna-mobilebar-alerts">
        <Bell className="w-5 h-5" />
        {alertsCount > 0 && <span className="badge">{alertsCount}</span>}
        <span>Alertas</span>
      </button>
      <button className={`dna-mobilebar-btn ${active === 'more' ? 'active' : ''}`} onClick={onMore} data-testid="dna-mobilebar-more">
        <span style={{ fontSize: 22, lineHeight: 1, fontWeight: 800 }}>☰</span>
        <span>Mais</span>
      </button>
    </nav>
  );
}

function DnaMobileMore({ open, onClose, onOpenSearch }) {
  const navigate = useNavigate();
  if (!open) return null;
  const go = (path) => { onClose(); setTimeout(() => navigate(path), 30); };
  const tiles = [
    { title: 'Desafio dos Campeões', sub: '🏆 Banco de Batalhas + Arena', path: '/v2/desafio-campeoes', Ico: Trophy },
    { title: 'Acadêmico', sub: 'Notas, turmas, frequência', path: '/v2/academico', Ico: BookOpen },
    { title: 'Financeiro', sub: 'DNAPay, cobranças, contratos', path: '/v2/financeiro', Ico: DollarSign },
    { title: 'Equipe', sub: 'Professores e funcionários', path: '/v2/secretaria', Ico: Users },
    { title: 'Eventos', sub: 'Festa Junina e calendário', path: '/v2/eventos', Ico: Calendar },
    { title: 'Comunicação', sub: 'Comunicados e mensagens', path: '/v2/comunicacoes', Ico: MessageCircle },
    { title: 'Pedagógico', sub: 'Recuperação e ocorrências', path: '/v2/pedagogico', Ico: ClipboardList },
    { title: 'IA Pendências', sub: 'Insights e ações sugeridas', path: '/v2/ia', Ico: Sparkles },
    { title: 'Configurações', sub: 'Perfil e preferências', path: '/admin', Ico: SettingsIcon },
  ];
  return (
    <div className="dna-mobilemore-overlay" onClick={onClose} data-testid="dna-mobilemore">
      <div className="dna-mobilemore" onClick={(e) => e.stopPropagation()}>
        <div className="dna-mobilemore-head">
          <div>
            <div style={{ fontSize: 11, letterSpacing: 2, color: '#8a93a6', textTransform: 'uppercase' }}>Menu</div>
            <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em' }}>Mais</div>
          </div>
          <button onClick={onClose} data-testid="dna-mobilemore-close" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, padding: '6px 10px', color: '#e6e8ee' }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <button className="dna-mobilemore-search" onClick={() => { onClose(); onOpenSearch && onOpenSearch(); }} data-testid="dna-mobilemore-search">
          <Search className="w-4 h-4" /> Pesquisar tudo…
        </button>

        <div className="dna-mobilemore-section">Módulos</div>
        <div className="dna-mobilemore-grid">
          {tiles.map((t) => {
            const Ico = t.Ico;
            return (
              <button key={t.title} className="dna-mobilemore-tile" onClick={() => go(t.path)} data-testid={`dna-mobilemore-tile-${t.title.toLowerCase()}`}>
                <Ico className="ico w-5 h-5" />
                <div className="title">{t.title}</div>
                <div className="sub">{t.sub}</div>
              </button>
            );
          })}
        </div>

        <div className="dna-mobilemore-section">Atalhos</div>
        <div className="dna-mobilemore-grid">
          <button className="dna-mobilemore-tile" onClick={() => go('/v3')} data-testid="dna-mobilemore-tile-home">
            <Activity className="ico w-5 h-5" />
            <div className="title">Centro Neural</div>
            <div className="sub">Home V3</div>
          </button>
          <button className="dna-mobilemore-tile" onClick={() => go('/v4/health')} data-testid="dna-mobilemore-tile-health">
            <ShieldCheck className="ico w-5 h-5" />
            <div className="title">Health Check</div>
            <div className="sub">Integridade dos dados</div>
          </button>
        </div>
      </div>
    </div>
  );
}

function DnaMobileQRFab() {
  const navigate = useNavigate();
  return (
    <button
      className="dna-mobile-qr-fab"
      onClick={() => navigate('/v2/festa-junina/checkin')}
      data-testid="dna-mobile-qr-fab"
      aria-label="Check-in QR"
      title="Check-in QR"
    >
      {/* Camera/QR glyph */}
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
        <circle cx="12" cy="13" r="4"></circle>
      </svg>
    </button>
  );
}

// ────────────────────────────────────  MOBILE GESTURES V4 (P3)  ────────────────────────────────────
// • Swipe direita desde a borda esquerda (≤ 24px) → voltar (navigate(-1))
// • Pull-to-refresh no topo da página → dispara evento global 'dna:refresh'
function DnaMobileGestures() {
  const navigate = useNavigate();
  const [pullY, setPullY] = useState(0);     // distância visual do pull (px)
  const [refreshing, setRefreshing] = useState(false);
  const startRef = useRef(null);             // { x, y, t, edge, mode }
  const PULL_THRESHOLD = 70;                 // px para disparar refresh
  const MAX_PULL = 120;                      // visual cap

  useEffect(() => {
    const isInteractive = (el) => {
      while (el && el !== document.body) {
        // não captura gestos dentro de overlays/modal/aluno360 (já tem swipe próprio)
        if (el.classList && (
          el.classList.contains('dnav3-360') ||
          el.classList.contains('dnav3-cmd') ||
          el.classList.contains('dnav3-360-overlay') ||
          el.classList.contains('dnav3-cmd-overlay') ||
          el.classList.contains('dna-mobilemore-overlay') ||
          el.classList.contains('dna-mobilebar')
        )) return true;
        el = el.parentElement;
      }
      return false;
    };

    const onStart = (e) => {
      const t = e.touches?.[0];
      if (!t) return;
      if (isInteractive(e.target)) { startRef.current = null; return; }
      const edge = t.clientX <= 24;
      const atTop = (window.scrollY || document.documentElement.scrollTop || 0) <= 1;
      startRef.current = { x: t.clientX, y: t.clientY, t: Date.now(), edge, atTop, mode: null };
    };

    const onMove = (e) => {
      const s = startRef.current;
      const t = e.touches?.[0];
      if (!s || !t) return;
      const dx = t.clientX - s.x;
      const dy = t.clientY - s.y;
      // Decide modo na primeira movimentação significativa
      if (!s.mode) {
        if (s.edge && dx > 12 && Math.abs(dy) < Math.abs(dx)) {
          s.mode = 'back';
        } else if (s.atTop && dy > 12 && Math.abs(dx) < Math.abs(dy)) {
          s.mode = 'refresh';
        } else if (Math.abs(dx) > 12 || Math.abs(dy) > 12) {
          s.mode = 'ignore';
        }
      }
      if (s.mode === 'refresh') {
        e.preventDefault?.();
        const y = Math.max(0, Math.min(MAX_PULL, dy * 0.6));
        setPullY(y);
      }
    };

    const onEnd = () => {
      const s = startRef.current;
      startRef.current = null;
      if (!s) return;
      if (s.mode === 'back') {
        // Swipe direita da borda esquerda = voltar
        if (window.history.length > 1) navigate(-1);
      } else if (s.mode === 'refresh') {
        if (pullY >= PULL_THRESHOLD) {
          setRefreshing(true);
          window.dispatchEvent(new CustomEvent('dna:refresh'));
          setTimeout(() => { setRefreshing(false); setPullY(0); }, 900);
        } else {
          setPullY(0);
        }
      }
    };

    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
    };
  }, [navigate, pullY]);

  if (pullY === 0 && !refreshing) return null;
  const progress = refreshing ? 1 : Math.min(1, pullY / PULL_THRESHOLD);
  return (
    <div
      className="dna-mobile-pull"
      data-testid="dna-mobile-pull"
      style={{ transform: `translate(-50%, ${refreshing ? 14 : Math.max(0, pullY - 30)}px)`, opacity: progress }}
    >
      <div className={`dna-mobile-pull-ring ${refreshing ? 'spin' : ''}`} style={{ transform: `rotate(${progress * 360}deg)` }} />
      <span className="dna-mobile-pull-lbl">{refreshing ? 'Atualizando…' : (progress >= 1 ? 'Solte para atualizar' : 'Puxe para atualizar')}</span>
    </div>
  );
}




// ────────────────────────────────────  COMMAND LAYER (global)  ────────────────────────────────────
export function DnaCommandLayer() {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [studentOverlay, setStudentOverlay] = useState(null);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [cheatOpen, setCheatOpen] = useState(false);
  const [alertsCount, setAlertsCount] = useState(0);
  const [moreOpen, setMoreOpen] = useState(false); // Mobile V4 — Menu Mais
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();

  // Portal da Equipe e DNA Connect (Portal das Famílias) NÃO devem mostrar a bottom
  // command bar nem o chrome mobile V4 (QR FAB, gestos, menu mais).
  // Mantém-se ativo em / (Home V4), /v3, /v4 e demais módulos.
  const isEquipePortal = location.pathname.startsWith('/equipe');
  const isFamiliaPortal = location.pathname.startsWith('/familia');
  // Gestão DNA (/gestao) tem chrome próprio e não pode exibir atalhos financeiros
  const isGestaoPortal = location.pathname.startsWith('/gestao');
  // Web administrativo /v2 (Layout2) tem shell mobile próprio: suprime a barra/menu/QR/gestos legados ali.
  const adminShell = useAdminShellActive();
  const hideMobileChrome = isEquipePortal || isFamiliaPortal || isGestaoPortal || adminShell;
  const isMainDashboard = location.pathname === '/v2/dashboard';

  const openSearch = useCallback(() => setPaletteOpen(true), []);
  const openStudent = useCallback((id) => setStudentOverlay(id || null), []);
  const openQuick = useCallback(() => setQuickOpen(true), []);

  // Atalho global ⌘K / Ctrl+K + Ctrl+Space para Aluno 360 + Ctrl+Shift+A Quick Actions + Ctrl+/ Cheatsheet
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        setCheatOpen((v) => !v);
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        setQuickOpen((v) => !v);
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
      if ((e.metaKey || e.ctrlKey) && (e.code === 'Space' || e.key === ' ')) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
      if (e.key === 'Escape') { setPaletteOpen(false); setAlertsOpen(false); setQuickOpen(false); setCheatOpen(false); }
    };
    window.addEventListener('keydown', handler);
    window.__dnaOpenSearch = () => setPaletteOpen(true);
    window.__dnaOpenStudent = (id) => setStudentOverlay(id);
    window.__dnaOpenAlerts = () => setAlertsOpen(true);
    window.__dnaOpenQuick = () => setQuickOpen(true);
    window.__dnaOpenCheatsheet = () => setCheatOpen(true);
    // V4 — eventos customizados do DnaHeaderV4
    const onEvSearch = () => setPaletteOpen(true);
    const onEvAlerts = () => setAlertsOpen(true);
    const onEvQuick = () => setQuickOpen(true);
    const onEvStudent = (e) => { if (e?.detail?.id) setStudentOverlay(e.detail.id); };
    window.addEventListener('dna:open-search', onEvSearch);
    window.addEventListener('dna:open-alerts', onEvAlerts);
    window.addEventListener('dna:open-quick', onEvQuick);
    window.addEventListener('dna:open-student', onEvStudent);
    return () => {
      window.removeEventListener('keydown', handler);
      window.removeEventListener('dna:open-search', onEvSearch);
      window.removeEventListener('dna:open-alerts', onEvAlerts);
      window.removeEventListener('dna:open-quick', onEvQuick);
      window.removeEventListener('dna:open-student', onEvStudent);
    };
  }, []);

  // Polling de alertas a cada 60s (cache leve)
  useEffect(() => {
    let alive = true;
    const fetch = () => api.get('/dnacore/v3/alerts').then((r) => alive && setAlertsCount(r.data.total || 0)).catch(() => { /* noop */ });
    fetch();
    const id = setInterval(fetch, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  return (
    <>
      {/* V9.14 · FAB "Aluno 360 · ⌃ Space" removido da interface por decisão do administrador.
          A página, a rota e o atalho Ctrl+Space continuam ativos. */}
      {!isFamiliaPortal && !isGestaoPortal && !isMainDashboard && (
        <FloatingDock
          onOpenSearch={openSearch}
          onOpenStudent={() => openSearch()}
          onOpenAlerts={() => setAlertsOpen(true)}
          onOpenQuick={openQuick}
          alertsCount={alertsCount}
        />
      )}
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} onPickStudent={openStudent} />
      <Aluno360Overlay studentId={studentOverlay} onClose={() => setStudentOverlay(null)} />
      <AlertsOverlay open={alertsOpen} onClose={() => setAlertsOpen(false)} />
      <QuickActionsDrawer open={quickOpen} onClose={() => setQuickOpen(false)} />
      <Cheatsheet open={cheatOpen} onClose={() => setCheatOpen(false)} />

      {/* DNACore Mobile V4 — bottom command bar, menu mais, QR FAB e gestos.
          Suprimido no Portal da Equipe (/equipe/*) e no DNA Connect (/familia/*) — Patches Ondas 11 e 12. */}
      {isMobile && !hideMobileChrome && (
        <>
          <DnaMobileBottomBar
            active={moreOpen ? 'more' : (quickOpen ? 'quick' : (alertsOpen ? 'alerts' : 'home'))}
            onHome={() => { setMoreOpen(false); navigate('/v3'); }}
            onStudent={openSearch}
            onQuick={() => { setMoreOpen(false); setQuickOpen(true); }}
            onAlerts={() => { setMoreOpen(false); setAlertsOpen(true); }}
            onMore={() => setMoreOpen(true)}
            alertsCount={alertsCount}
          />
          <DnaMobileMore open={moreOpen} onClose={() => setMoreOpen(false)} onOpenSearch={openSearch} />
          {!isMainDashboard && <DnaMobileQRFab />}
          <DnaMobileGestures />
        </>
      )}
    </>
  );
}

// ────────────────────────────────────  SIDEBAR V3.5 — Iconic DNA  ────────────────────────────────────
const PROFILE_PRIORITY = {
  professor: ['op-acad', 'a-notas', 'a-recup', 'op-events'],
  financeiro: ['op-fin', 'a-inad', 'a-docs', 'op-events'],
  direcao: [],
};

function DnaOrb({ status, alertsCount, onClick }) {
  const color = status === 'bad' ? '#ef4444' : status === 'warn' ? '#f59e0b' : '#22c55e';
  return (
    <button onClick={onClick} className="dnav3-orb-btn" data-testid="dnav3-orb" title="DNA Command Center">
      <span className="dnav3-orb" style={{ '--orb-color': color }}>
        <span className="dnav3-orb-core" />
        <span className="dnav3-orb-ring" />
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.1 }}>
        <span style={{ fontWeight: 800, letterSpacing: '-0.01em', fontSize: 14 }}>DNACore</span>
        <span style={{ fontSize: 10, color: 'var(--dna-fg-dim)', letterSpacing: 2, textTransform: 'uppercase' }}>
          {status === 'ok' ? 'Sistema saudável' : status === 'warn' ? `${alertsCount} alerta(s)` : 'Atenção crítica'}
        </span>
      </span>
    </button>
  );
}

export function DnaSidebarV3({ active }) {
  const navigate = useNavigate();
  const [radar, setRadar] = useState(null);
  const [today, setToday] = useState(null);
  const [alerts, setAlerts] = useState({ total: 0 });
  const [compact, setCompact] = useState(() => readLS('dna_v35_sidebar_compact', false));
  const [hoverItem, setHoverItem] = useState(null);
  const profile = (() => {
    try { return (JSON.parse(localStorage.getItem('user') || '{}').role || 'direcao').toLowerCase(); }
    catch { return 'direcao'; }
  })();

  useEffect(() => {
    let alive = true;
    const fetch = () => {
      api.get('/dnacore/v3/radar').then((r) => alive && setRadar(r.data)).catch(() => { /* noop */ });
      api.get('/dnacore/v3/today').then((r) => alive && setToday(r.data)).catch(() => { /* noop */ });
      api.get('/dnacore/v3/alerts').then((r) => alive && setAlerts(r.data || { total: 0 })).catch(() => { /* noop */ });
    };
    fetch();
    const id = setInterval(fetch, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const status = alerts.total > 5 ? 'bad' : alerts.total > 0 ? 'warn' : 'ok';
  const toggleCompact = () => { setCompact((v) => { writeLS('dna_v35_sidebar_compact', !v); return !v; }); };

  const modules = [
    { id: 'op-acad', label: 'Acadêmico', icon: GraduationCap, hint: `${radar?.academico ?? '—'}%`, preview: { 'Notas lançadas': `${radar?.academico ?? '—'}%`, 'Pendências': radar?.pendencias ?? '—', 'Recuperações': today?.recuperacoes ?? '—' }, go: '/v2/academico' },
    { id: 'op-fin', label: 'Financeiro', icon: Wallet, hint: `R$ ${Number(today?.recebimentos_hoje || 0).toFixed(0)}`, preview: { 'Recebido hoje': `R$ ${Number(today?.recebimentos_hoje || 0).toFixed(2)}`, 'Performance': `${radar?.financeiro ?? '—'}%` }, go: '/v2/financeiro' },
    { id: 'op-dnapay', label: 'DNAPay', icon: CreditCard, hint: `${radar?.cobranca ?? '—'}%`, preview: { 'Cobrança': `${radar?.cobranca ?? '—'}%`, 'Vencidos': alerts.groups?.find?.((g) => g.key === 'financeiro')?.items?.length || 0 }, go: '/v2/financeiro' },
    { id: 'op-team', label: 'Equipe', icon: Users, hint: today?.professores_presentes != null ? `${today.professores_presentes} presentes` : '—', preview: { 'Professores presentes': today?.professores_presentes ?? '—' }, go: '/v2/secretaria' },
    { id: 'op-events', label: 'Eventos', icon: Calendar, hint: `${today?.eventos_hoje ?? 0} hoje`, preview: { 'Hoje': today?.eventos_hoje ?? 0, 'Total': radar?.eventos ?? 0, 'Check-ins': today?.checkins_hoje ?? 0 }, go: '/v2/eventos' },
    { id: 'op-champions', label: 'Desafio Campeões', icon: Trophy, hint: '🏆', preview: { 'Modo Palco': 'TV / Datashow', 'Banco': 'Batalhas + Arena' }, go: '/v2/desafio-campeoes' },
    { id: 'op-comm', label: 'Comunicação', icon: MessageCircle, hint: '', go: '/v2/comunicacoes' },
    { id: 'cfg', label: 'Configurações', icon: SettingsIcon, hint: '', go: '/admin' },
  ];
  const priority = PROFILE_PRIORITY[profile] || [];
  const sortedModules = [...modules].sort((a, b) => {
    const ai = priority.indexOf(a.id); const bi = priority.indexOf(b.id);
    return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
  });

  return (
    <aside className={`dnav3-sidebar ${compact ? 'compact' : ''}`} data-testid="dnav3-sidebar">
      {/* DNA Orb */}
      <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid var(--dna-line)' }}>
        <DnaOrb status={status} alertsCount={alerts.total} onClick={() => navigate('/v3')} />
      </div>

      {/* ALUNO 360 HERO */}
      <button
        onClick={() => window.__dnaOpenSearch && window.__dnaOpenSearch()}
        className="dnav3-aluno360-hero"
        data-testid="dnav3-sidebar-aluno360"
        title="Aluno 360 · ⌃Space"
      >
        <span className="hero-helix" />
        {!compact && (
          <span style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
            <span style={{ fontWeight: 900, letterSpacing: '-0.02em', fontSize: 16 }}>ALUNO 360</span>
            <span style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.65)' }}>Acesse qualquer aluno instantaneamente</span>
            <span style={{ fontSize: 9.5, color: '#fbbf24', letterSpacing: 1.5, marginTop: 3 }}>⌃ SPACE</span>
          </span>
        )}
      </button>

      {/* QUICK ACTIONS + ALERTS */}
      {!compact && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, padding: '10px 12px 4px' }}>
          <button onClick={() => window.__dnaOpenQuick && window.__dnaOpenQuick()} className="dnav3-mini-btn" data-testid="dnav3-sb-quick">
            <Zap className="w-3.5 h-3.5" style={{ color: '#fbbf24' }} /> Ações
          </button>
          <button onClick={() => window.__dnaOpenAlerts && window.__dnaOpenAlerts()} className="dnav3-mini-btn" data-testid="dnav3-sb-alerts" style={{ position: 'relative' }}>
            <Bell className="w-3.5 h-3.5" /> Alertas
            {alerts.total > 0 && <span className="dnav3-mini-badge">{alerts.total}</span>}
          </button>
        </div>
      )}

      {/* MINI RADAR */}
      {!compact && (
        <>
          <div className="dnav3-section-title">Radar Institucional</div>
          <RadarMini data={radar} navigate={navigate} />
        </>
      )}

      {/* Módulos */}
      {!compact && <div className="dnav3-section-title">Operação</div>}
      {sortedModules.map((it) => {
        const Icon = it.icon;
        const isHover = hoverItem === it.id;
        return (
          <div
            key={it.id}
            className={`dnav3-item ${active === it.id ? 'active' : ''}`}
            onClick={() => it.go && navigate(it.go)}
            onMouseEnter={() => setHoverItem(it.id)}
            onMouseLeave={() => setHoverItem(null)}
            title={compact ? it.label : ''}
            data-testid={`dnav3-mod-${it.id}`}
            style={{ position: 'relative' }}
          >
            <Icon className="ic" />
            {!compact && <span>{it.label}</span>}
            {!compact && it.hint && <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--dna-fg-dim)', fontVariantNumeric: 'tabular-nums' }}>{it.hint}</span>}
            {!compact && isHover && it.preview && (
              <div className="dnav3-preview" data-testid={`dnav3-preview-${it.id}`}>
                <div style={{ fontWeight: 800, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: '#a5b4fc', marginBottom: 6 }}>{it.label}</div>
                {Object.entries(it.preview).map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0' }}>
                    <span style={{ color: 'var(--dna-fg-dim)' }}>{k}</span>
                    <strong style={{ color: '#fff', fontFamily: 'ui-monospace,monospace' }}>{v}</strong>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* DNA Dock (rodapé) */}
      <div className="dnav3-sb-dock" data-testid="dnav3-sb-dock">
        <button onClick={() => window.__dnaOpenSearch && window.__dnaOpenSearch()} title="Busca Global ⌘K"><Search className="w-4 h-4" /></button>
        <button onClick={() => window.open('https://web.whatsapp.com', '_blank')} title="WhatsApp"><MessageCircle className="w-4 h-4" /></button>
        <button onClick={() => window.open('mailto:', '_blank')} title="E-mail"><Mail className="w-4 h-4" /></button>
        <button onClick={() => window.__dnaOpenCheatsheet && window.__dnaOpenCheatsheet()} title="Ajuda ⌘/"><Sparkles className="w-4 h-4" /></button>
        <button onClick={toggleCompact} title={compact ? 'Expandir' : 'Compactar'} style={{ marginLeft: 'auto', color: '#fbbf24' }}>
          {compact ? '▶' : '◀'}
        </button>
      </div>
    </aside>
  );
}
// ────────────────────────────────────  HOME V3  ────────────────────────────────────
export default function HomeV3() {
  const [today, setToday] = useState(null);
  const [radar, setRadar] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    let alive = true;
    const fetch = () => {
      api.get('/dnacore/v3/today').then((r) => alive && setToday(r.data)).catch(() => { /* noop */ });
      api.get('/dnacore/v3/radar').then((r) => alive && setRadar(r.data)).catch(() => { /* noop */ });
      api.get('/dnacore/v3/alerts').then((r) => alive && setAlerts(r.data)).catch(() => { /* noop */ });
    };
    fetch();
    const id = setInterval(fetch, 30000);
    // Mobile V4 — pull-to-refresh dispara `dna:refresh`
    const onRefresh = () => fetch();
    window.addEventListener('dna:refresh', onRefresh);
    return () => { alive = false; clearInterval(id); window.removeEventListener('dna:refresh', onRefresh); };
  }, []);

  const cards = [
    { lbl: 'Alunos ativos', val: today?.alunos_ativos, ico: Users },
    { lbl: 'Professores presentes', val: today?.professores_presentes, ico: GraduationCap },
    { lbl: 'Notas lançadas hoje', val: today?.notas_lancadas_hoje, ico: FileText },
    { lbl: 'Recuperações', val: today?.recuperacoes, ico: AlertTriangle },
    { lbl: 'Eventos de hoje', val: today?.eventos_hoje, ico: Calendar },
    { lbl: 'Check-ins hoje', val: today?.checkins_hoje, ico: Activity },
    { lbl: 'Recebimentos hoje', val: today?.recebimentos_hoje != null ? `R$ ${Number(today.recebimentos_hoje).toFixed(2)}` : null, ico: DollarSign },
    { lbl: 'IA Pendências', val: radar?.pendencias, ico: Sparkles },
  ];

  const now = new Date();
  const data = now.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <div className="dnav3-home">
      <NeuralBackground />
      <div style={{ position: 'relative', zIndex: 1, maxWidth: 1400, margin: '0 auto', padding: '3.5rem 2.5rem 3rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', color: '#8a93a6' }}>Hoje no Novo DNA</div>
            <div className="hero" style={{ marginTop: 6 }}>Centro Neural da Escola</div>
            <div style={{ color: '#8a93a6', marginTop: 6, textTransform: 'capitalize' }}>{data}</div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => window.__dnaOpenQuick && window.__dnaOpenQuick()}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 16px', borderRadius: 14,
                background: 'linear-gradient(135deg, rgba(251,191,36,0.18), rgba(255,59,107,0.18))',
                border: '1px solid rgba(251,191,36,0.4)',
                color: '#fff', cursor: 'pointer', fontWeight: 700,
              }}
              data-testid="home-v3-open-quick"
            >
              <Zap className="w-4 h-4" style={{ color: '#fbbf24' }} /> Ações rápidas <span style={{ fontSize: 10, color: '#8a93a6' }}>⌘⇧A</span>
            </button>
            <button
              onClick={() => window.__dnaOpenSearch && window.__dnaOpenSearch()}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 16px', borderRadius: 14,
                background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)',
                color: '#e6e8ee', cursor: 'pointer', fontWeight: 600,
              }}
              data-testid="home-v3-open-search"
            >
              <Search className="w-4 h-4" /> Pesquisar tudo… <span style={{ fontSize: 10, color: '#8a93a6' }}>⌘K</span>
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginTop: 28 }}>
          {cards.map((c) => {
            const Icon = c.ico;
            const isLoading = c.val === undefined || c.val === null;
            return (
              <div key={c.lbl} className="dnav3-card-v3">
                <Icon className="ico w-7 h-7" />
                <div className="lbl">{c.lbl}</div>
                {isLoading ? <div className="dnav3-skel" style={{ height: 36, marginTop: 6 }} /> : <div className="num">{c.val}</div>}
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 36, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
          <div className="dnav3-card-v3" data-testid="dnav3-atencao-hoje">
            <div className="lbl" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <AlertTriangle className="w-3.5 h-3.5" style={{ color: '#fbbf24' }} /> O que precisa da sua atenção hoje
            </div>
            {!alerts && <div className="dnav3-skel" style={{ height: 80, marginTop: 10 }} />}
            {alerts && alerts.total === 0 && (
              <div style={{ marginTop: 10, color: '#86efac', fontSize: 13 }}>✓ Nenhuma prioridade no momento.</div>
            )}
            {alerts && alerts.total > 0 && (
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {alerts.groups.flatMap((g) => g.items.map((it, i) => ({ ...it, area: g.key, k: `${g.key}-${i}` }))).slice(0, 6).map((it) => (
                  <div key={it.k} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <span style={{ width: 8, height: 8, borderRadius: 999, background: it.severity === 'bad' ? '#ef4444' : it.severity === 'warn' ? '#f59e0b' : '#22c55e' }} />
                    <span style={{ fontSize: 13, color: '#cbd5e1', flex: 1 }}>{it.label}</span>
                    <span style={{ fontSize: 10, color: '#8a93a6', textTransform: 'capitalize' }}>{it.area}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="dnav3-card-v3">
            <div className="lbl">Radar Institucional</div>
            <div style={{ marginTop: 12 }}>
              <RadarMini data={radar} navigate={navigate} />
            </div>
          </div>
          <div className="dnav3-card-v3">
            <div className="lbl">Atalhos</div>
            <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {['Acadêmico', 'Financeiro', 'Eventos', 'Comunicações', 'Pedagógico'].map((t) => (
                <span key={t} style={{ fontSize: 12, padding: '5px 10px', borderRadius: 999, border: '1px solid rgba(255,255,255,0.12)', color: '#cbd5e1' }}>{t}</span>
              ))}
            </div>
          </div>
          <div className="dnav3-card-v3">
            <div className="lbl">Identidade DNA</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
              <div style={{ width: 50, height: 50, borderRadius: 14, background: 'radial-gradient(circle at 35% 35%, #fbbf24, #ff3b6b 60%, #6366f1)', boxShadow: '0 0 24px rgba(255,59,107,0.4)' }} />
              <div>
                <div style={{ fontWeight: 800, letterSpacing: '-0.01em' }}>Muitas mentes, Uma missão.</div>
                <div style={{ fontSize: 11, color: '#8a93a6' }}>Inteligência · Conexões · Gestão</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
